<template>
  <el-dialog
    width="50%"
    :title="note && note.flagged ? 'Unflag Voice Note' : 'Flag for Review'"
    destroy-on-close
    :visible.sync="internalVisible"
    @close="handleClose"
  >
    <div v-if="note">
      <div class="modal-meta">
        <div class="meta-item">
          <i class="el-icon-user"></i>
          <span
            ><strong>{{ note.sender }}</strong></span
          >
        </div>
        <div class="meta-item" v-if="note.timestamp">
          <i class="el-icon-time"></i>
          <span>{{ formatDate(note.timestamp) }}</span>
        </div>
        <el-tag
          v-if="note.flagged"
          size="mini"
          type="danger"
          effect="dark"
          style="margin-left: auto"
        >
          <i class="fa fa-flag"></i> Currently Flagged
        </el-tag>
        <el-tag v-else size="mini" type="info" effect="plain" style="margin-left: auto">
          <i class="fa fa-flag-o"></i> Not Flagged
        </el-tag>
      </div>

      <el-card class="transcript-card" shadow="never">
        <div slot="header" class="card-header">
          <i class="el-icon-document"></i> Transcript Preview
        </div>
        <span class="transcript-text">{{ previewText }}</span>
      </el-card>

      <div v-if="note.flagged" class="previous-flag">
        <el-divider content-position="left">
          <i class="fa fa-flag"></i> Previous Flag Info
        </el-divider>
        <div class="flag-info">
          <div class="flag-info-item" v-if="note.flaggedBy">
            <span class="flag-label">Flagged by</span>
            <el-tag size="mini" type="danger" effect="plain">
              <i class="el-icon-user"></i> {{ note.flaggedBy }}
            </el-tag>
          </div>
          <div class="flag-info-item" v-if="note.flaggedAt">
            <span class="flag-label">Flagged on</span>
            <el-tag size="mini" type="info" effect="plain">
              <i class="el-icon-time"></i> {{ formatDate(note.flaggedAt) }}
            </el-tag>
          </div>
          <div class="flag-info-item" v-if="note.flagReason">
            <span class="flag-label">Comment</span>
            <span class="flag-reason">{{ note.flagReason }}</span>
          </div>
        </div>
      </div>

      <el-divider />

      <el-form label-position="top" size="small">
        <el-form-item label="Action By">
          <el-input :value="actionBy" disabled>
            <i slot="prefix" class="el-icon-user el-input__icon"></i>
          </el-input>
        </el-form-item>

        <el-form-item label="Date">
          <el-input :value="actionDate" disabled>
            <i slot="prefix" class="el-icon-date el-input__icon"></i>
          </el-input>
        </el-form-item>

        <el-form-item label="Reason / Comments (optional)" v-if="!note.flagged">
          <el-input
            type="textarea"
            v-model="reason"
            placeholder="Leave a reason or comment..."
            :rows="3"
            maxlength="300"
            show-word-limit
          />
        </el-form-item>
      </el-form>
    </div>

    <div slot="footer" class="dialog-footer">
      <el-button size="small" @click="handleClose">Cancel</el-button>
      <el-button
        v-if="note && note.flagged"
        size="small"
        type="primary"
        :loading="loading"
        @click="handleFlag(false)"
      >
        Unflag Voice Note
      </el-button>
      <el-button
        v-else
        size="small"
        type="danger"
        icon="fa fa-flag"
        :loading="loading"
        @click="handleFlag(true)"
      >
        Flag for Review
      </el-button>
    </div>
  </el-dialog>
</template>

<script>
import moment from 'moment';
import { mapState } from 'vuex';

export default {
  name: 'FlagModal',
  props: {
    visible: { type: Boolean, required: true },
    note: { type: Object, default: null },
    loading: { type: Boolean, default: false },
  },
  data() {
    return {
      reason: '',
    };
  },
  computed: {
    ...mapState('user', ['me']),
    internalVisible: {
      get() {
        return this.visible;
      },
      set(v) {
        this.$emit('update:visible', v);
      },
    },
    actionBy() {
      return this.me ? this.me.display_name : '';
    },
    actionDate() {
      return moment().format('DD MMM YYYY, HH:mm');
    },
    previewText() {
      if (!this.note || !this.note.transcript) return 'No transcript available.';
      return this.note.transcript.length > 150
        ? `${this.note.transcript.slice(0, 150)}...`
        : this.note.transcript;
    },
  },
  methods: {
    formatDate(timestamp) {
      return moment(timestamp).format('DD MMM YYYY, HH:mm');
    },
    handleFlag(flagStatus) {
      this.$emit('flag', {
        id: this.note.id,
        flagStatus,
        flaggedBy: this.me.display_name,
        flaggedById: this.me.id,
        reason: this.reason,
      });

      if (this.reason && this.reason.trim()) {
        this.$store.dispatch('interaction/sendComment', {
          interactionId: this.note.id,
          message: this.reason,
        });
      }
    },
    handleClose() {
      this.reason = '';
      this.internalVisible = false;
      this.$emit('close');
    },
  },
};
</script>

<style scoped>
.modal-meta {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px;
  background: #f9f9f9;
  border-radius: 6px;
}

.meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 13px;
  color: #606266;
}

.transcript-card {
  margin-bottom: 16px;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #606266;
}

.transcript-text {
  font-size: 13px;
  line-height: 1.6;
  color: #606266;
  font-style: italic;
}

.previous-flag {
  margin-bottom: 8px;
}

.flag-info {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 8px 12px;
  background: #fff5f5;
  border-radius: 6px;
  border: 1px solid #fde2e2;
}

.flag-info-item {
  display: flex;
  align-items: center;
  gap: 8px;
}

.flag-label {
  font-size: 12px;
  color: #909399;
  min-width: 80px;
}

.flag-reason {
  font-size: 13px;
  color: #606266;
  font-style: italic;
}

.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}
</style>


// WEBPACK FOOTER //
// FlagModal.vue?93578da8